import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, ArrowUpFromLine, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, LoadingBlock, EmptyState, formatDateTime, formatCurrency } from "@/components/Common";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";

const STATUSES = ["SUBMITTED", "PENDING_REVIEW", "ADDITIONAL_VERIFICATION_REQUIRED", "APPROVED", "PROCESSING", "COMPLETED", "REJECTED", "CANCELLED", "FAILED"];

export default function AdminWithdrawals() {
  const { lang } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de ?? en ?? it) : it);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const load = useCallback(async () => {
    const { data } = await api.get("/withdrawals", { params: { q: q || undefined, status: status || undefined } });
    setItems(data);
  }, [q, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const act = async (w, action, extra = {}) => {
    try { await api.patch(`/admin/withdrawals/${w.id}`, { action, ...extra }); toast.success(L("Aggiornato", "Updated", "Aktualisiert")); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-heading text-2xl font-medium text-white">{L("Prelievi", "Withdrawals", "Auszahlungen")}</h2>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Cerca ID o destinazione…", "Search ID or destination…", "ID oder Ziel suchen…")} className="pl-9 bg-navy-paper border-white/10" data-testid="wd-search" /></div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md bg-navy-paper border border-white/10 px-3 text-sm text-white" data-testid="wd-status-filter"><option value="">{L("Tutti", "All", "Alle")}</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
      </div>
      {items === null ? <LoadingBlock /> : items.length === 0 ? <EmptyState icon={ArrowUpFromLine} title={L("Nessun prelievo", "No withdrawals", "Keine Auszahlungen")} /> : (
        <div className="space-y-3">
          {items.map((w) => (
            <Card key={w.id} className="p-5" data-testid={`admin-wd-${w.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="text-white font-mono text-sm">{w.id.slice(0, 8)} · {formatCurrency(w.amount, w.currency)}</p>
                  <p className="text-xs text-slate-500 mt-1">{L("Netto", "Net", "Netto")}: {formatCurrency(w.net_amount, w.currency)} · {L("Commissione", "Commission", "Provision")}: {formatCurrency(w.commission_fee, w.currency)} + {formatCurrency(w.network_fee, w.currency)}</p>
                  <p className="text-xs text-slate-500">{w.destination_type} · <span className="font-mono">{w.destination}</span></p>
                  <p className="text-xs text-slate-600 mt-1">{formatDateTime(w.created_at)} · <span className="text-gold">{w.status}</span></p>
                </div>
                {["SUBMITTED", "PENDING_REVIEW", "ADDITIONAL_VERIFICATION_REQUIRED", "APPROVED", "PROCESSING"].includes(w.status) && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" className="border-white/20 h-8" onClick={() => act(w, "request_verification", { note: "Verifica richiesta" })} data-testid={`wd-verify-${w.id}`}>{L("Verifica", "Verify", "Prüfen")}</Button>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 h-8" onClick={() => act(w, "approve", { note: "" })} data-testid={`wd-approve-${w.id}`}>{L("Approva", "Approve", "Genehmigen")}</Button>
                    <RejectBtn w={w} onReject={(explanation, note) => act(w, "reject", { client_explanation: explanation, note })} L={L} />
                    {w.status === "APPROVED" && <Button size="sm" variant="outline" className="border-white/20 h-8" onClick={() => act(w, "processing")} data-testid={`wd-processing-${w.id}`}>{L("In elaborazione", "Processing", "In Bearbeitung")}</Button>}
                    {["APPROVED", "PROCESSING"].includes(w.status) && <CompleteBtn w={w} onComplete={(ref) => act(w, "complete", { payment_reference: ref })} L={L} />}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function RejectBtn({ onReject, L }) {
  const [show, setShow] = useState(false);
  const [exp, setExp] = useState("");
  const [note, setNote] = useState("");
  if (!show) return <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 h-8" onClick={() => setShow(true)}>{L("Rifiuta", "Reject", "Ablehnen")}</Button>;
  return (
    <div className="w-full mt-2 space-y-2 rounded-lg border border-red-500/20 bg-red-500/5 p-3">
      <Textarea placeholder={L("Spiegazione per il cliente", "Client explanation", "Erklärung für den Kunden")} value={exp} onChange={(e) => setExp(e.target.value)} rows={2} className="bg-navy border-white/10" />
      <Textarea placeholder={L("Nota interna", "Internal note", "Interne Notiz")} value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="bg-navy border-white/10" />
      <div className="flex gap-2"><Button size="sm" className="bg-red-600 hover:bg-red-500 h-8" disabled={!exp || !note} onClick={() => onReject(exp, note)}>{L("Conferma rifiuto", "Confirm reject", "Ablehnung bestätigen")}</Button><Button size="sm" variant="ghost" className="h-8" onClick={() => setShow(false)}>{L("Annulla", "Cancel", "Abbrechen")}</Button></div>
    </div>
  );
}

function CompleteBtn({ onComplete, L }) {
  const [show, setShow] = useState(false);
  const [ref, setRef] = useState("");
  if (!show) return <Button size="sm" className="bg-gold text-navy hover:bg-gold-hover h-8" onClick={() => setShow(true)}>{L("Completa", "Complete", "Abschließen")}</Button>;
  return (
    <div className="w-full mt-2 flex gap-2">
      <Input placeholder={L("Riferimento transazione", "Transaction reference", "Transaktionsreferenz")} value={ref} onChange={(e) => setRef(e.target.value)} className="bg-navy border-white/10 h-8" />
      <Button size="sm" className="bg-gold text-navy h-8" onClick={() => onComplete(ref)}>{L("Conferma", "Confirm", "Bestätigen")}</Button>
    </div>
  );
}
